/**
 * RequireIdentity — site-wide gate.
 *
 * Wraps any route that should be inaccessible to signed-out users.
 * If no identity is present in localStorage, we render an inline
 * "signed out" panel with a single CTA that opens the LightSignIn
 * modal. After signing in, the children render.
 *
 * The actual sign-in modal still mounts at the App layer so users
 * who click "Sign in" from the description page get the same form.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getIdentity } from "../lib/learnerIdentity";
import { isMasterUnlocked, verifyMasterCode } from "../lib/masterUnlock";
import LightSignIn from "./LightSignIn";

const ACCENT = "#6B8F71";
const ACCENT_OK = "#6B8F71";
const ACCENT_WARM = "#6B8F71";

export default function RequireIdentity({ children }) {
  const [identity, setIdentity] = useState(() => getIdentity());
  const [masterOk, setMasterOk] = useState(() => isMasterUnlocked());
  const [openSignIn, setOpenSignIn] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);

  // Re-check on every mount in case the user signed out elsewhere
  // (e.g. via the header pill on a different tab). Also listen for the
  // same-tab "maldek-identity-change" custom event so the gate lifts
  // the instant LightSignIn writes the identity.
  useEffect(() => {
    const onStorage = () => {
      setIdentity(getIdentity());
      setMasterOk(isMasterUnlocked());
    };
    const onIdentity = () => {
      setIdentity(getIdentity());
      setMasterOk(isMasterUnlocked());
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("maldek-identity-change", onIdentity);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("maldek-identity-change", onIdentity);
    };
  }, []);

  const submitMaster = async (e) => {
    e?.preventDefault();
    if (codeBusy) return;
    setCodeBusy(true);
    setCodeErr("");
    const res = await verifyMasterCode(code);
    if (res.ok) setMasterOk(true);
    else setCodeErr("Code rejected.");
    setCodeBusy(false);
  };

  if (identity) return children;
  if (masterOk) return children;

  return (
    <>
      {openSignIn && (
        <LightSignIn
          forceOpen
          onSignedIn={(id) => { setIdentity(id); setOpenSignIn(false); }}
        />
      )}
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 12% -10%, rgba(107,143,113,0.18), transparent 60%),
            radial-gradient(ellipse 70% 50% at 100% 110%, rgba(167,139,250,0.13), transparent 60%),
            #060b1c
          `,
        }}
        data-testid="require-identity-gate"
      >
        <div
          className="w-full max-w-lg rounded-3xl border p-8 text-center"
          style={{
            background: `radial-gradient(circle at 0% 0%, ${ACCENT}22, transparent 55%), rgba(10,18,38,0.95)`,
            borderColor: `${ACCENT}44`,
          }}
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.28em] mb-3" style={{ color: ACCENT_OK }}>
            Sign in required
          </p>
          <h2 className="font-heading text-3xl text-white font-bold tracking-tight mb-3">
            This area is members-only.
          </h2>
          <p className="text-white/70 text-sm mb-6 leading-relaxed">
            The BLS Academy, ACLS Academy, Clinical Reasoning, and
            Community modules track activity to your learner profile. Sign in
            with email and name to continue.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={() => setOpenSignIn(true)}
              data-testid="require-identity-signin"
              className="px-6 py-3 rounded-full text-sm font-mono uppercase tracking-widest font-bold transition-all"
              style={{
                background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_OK})`,
                color: "#06121e",
                boxShadow: `0 4px 20px ${ACCENT}55`,
              }}
            >
              Sign in
            </button>
            <Link
              to="/"
              data-testid="require-identity-back"
              className="px-6 py-3 rounded-full text-sm font-mono uppercase tracking-widest border border-white/15 text-white/75 hover:text-white hover:border-white/40 transition-colors"
            >
              ← Back to overview
            </Link>
          </div>

          {/* Master-code fast path for admins / QA. */}
          <div className="mt-6 pt-5 border-t border-white/8">
            {!codeOpen ? (
              <button
                type="button"
                onClick={() => setCodeOpen(true)}
                data-testid="require-identity-master-link"
                className="text-[11px] font-mono uppercase tracking-[0.22em] hover:opacity-80"
                style={{ color: ACCENT_WARM }}
              >
                I have a master code →
              </button>
            ) : (
              <form onSubmit={submitMaster} className="flex flex-col gap-2 max-w-sm mx-auto" data-testid="require-identity-master-form">
                <p className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: ACCENT_WARM }}>
                  Master code · admin only
                </p>
                <input
                  type="password"
                  value={code}
                  onChange={(e) => { setCode(e.target.value); setCodeErr(""); }}
                  placeholder="9-digit master code"
                  autoFocus
                  inputMode="numeric"
                  data-testid="require-identity-master-input"
                  className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/15 text-white text-base font-mono tracking-widest focus:outline-none focus:border-[#6B8F71]/60 transition-colors text-center"
                />
                {codeErr && (
                  <p className="text-red-400 text-xs" data-testid="require-identity-master-error">{codeErr}</p>
                )}
                <button
                  type="submit"
                  disabled={codeBusy || !code}
                  data-testid="require-identity-master-submit"
                  className="py-2.5 rounded-full text-xs font-mono uppercase tracking-widest font-bold disabled:opacity-40 transition-all"
                  style={{
                    background: `linear-gradient(135deg, ${ACCENT_WARM}, ${ACCENT_OK})`,
                    color: "#06121e",
                  }}
                >
                  {codeBusy ? "Verifying…" : "Unlock"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
